import React, { useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { Table } from "antd"
import { useSelector } from "react-redux"
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable"
import { routes } from "router/routes"
import { useTranslation } from "react-i18next"
import { createEquipmentColumns } from "./columns"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { DSIcon } from "ui/Icon/DSIcon"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import SortableTableRow, { DragHandleCell, SortableDisabledContext } from "ui/SortableTableRow"
import styles from "./index.module.css"

export const dataSource = []

const dragHandleColumn = {
  key: "drag",
  width: 40,
  render: () => <DragHandleCell />
}

const EquipmentTable = ({ data = dataSource, onChange, onDeactivate, onDelete, onActivate, onAssignClick, onReorder, dragDisabled, loading }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const userRole = useSelector((state) => state.user.role)

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  )

  const handleEdit = useCallback((record) => {
    navigate(routes.guideEquipmentEditPath({ id: record.key }))
  }, [navigate])

  const handleDuplicate = useCallback((record) => {
    navigate(routes.guideEquipmentDuplicatePath({ id: record.key }))
  }, [navigate])

  const handleTitleClick = useCallback(
    (record) => {
      navigate(routes.guideEquipmentDetailsPath({ id: record.key }))
    },
    [navigate]
  )

  const dataColumns = useMemo(
    () =>
      createEquipmentColumns(
        { onEdit: handleEdit, onDeactivate, onDelete, onActivate, onDuplicate: handleDuplicate, onTitleClick: handleTitleClick, onAssignClick },
        t,
        { hasPermission, MODULES, ACTIONS, userRole }
      ),
    [handleEdit, onDeactivate, onDelete, onActivate, handleDuplicate, handleTitleClick, onAssignClick, t, hasPermission, userRole]
  )

  const columns = useMemo(() => [dragHandleColumn, ...dataColumns], [dataColumns])

  const [liveIds, setLiveIds] = useState(null)
  const liveIdsRef = React.useRef(null)
  const lastOverIdRef = React.useRef(null)

  const baseIds = useMemo(() => data.map((item) => String(item.key ?? item.id)), [data])
  const sortableIds = liveIds ?? baseIds

  const liveData = useMemo(() => {
    if (!liveIds) return data
    const map = new Map(data.map((item) => [String(item.key ?? item.id), item]))
    return liveIds.map((id) => map.get(id)).filter(Boolean)
  }, [liveIds, data])

  const handleDragOver = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return
      if (lastOverIdRef.current === String(over.id)) return
      lastOverIdRef.current = String(over.id)
      const ids = liveIdsRef.current ?? baseIds
      const oldIndex = ids.indexOf(String(active.id))
      const newIndex = ids.indexOf(String(over.id))
      if (oldIndex === -1 || newIndex === -1) return
      const next = arrayMove(ids, oldIndex, newIndex)
      liveIdsRef.current = next
      setLiveIds(next)
    },
    [baseIds]
  )

  const handleDragEnd = useCallback(
    ({ active }) => {
      const finalIds = liveIdsRef.current
      liveIdsRef.current = null
      lastOverIdRef.current = null
      setLiveIds(null)
      if (!finalIds) return
      const activeId = String(active.id)
      const oldIndex = baseIds.indexOf(activeId)
      const newIndex = finalIds.indexOf(activeId)
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return
      // overId is the item currently occupying the target slot in the original order
      const overId = baseIds[newIndex]
      if (!overId) return
      onReorder?.(activeId, overId)
    },
    [onReorder, baseIds]
  )

  const handleDragCancel = useCallback(() => {
    liveIdsRef.current = null
    lastOverIdRef.current = null
    setLiveIds(null)
  }, [])

  return (
    <section className={styles.equipmentTable}>
      <WithEmptyStateWrapper
        name="equipment"
        hasItems={data?.length > 0}
        fallbackTitle={t("equipment.empty_state.title", { defaultValue: "No equipment added yet" })}
        fallbackDescription={t("equipment.empty_state.description", { defaultValue: "Add your first equipment to get started" })}
        fallbackImageSrc={<DSIcon name="boat" width={80} height={80} />}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <SortableDisabledContext.Provider value={Boolean(dragDisabled)}>
              <Table
                columns={columns}
                dataSource={liveData}
                pagination={false}
                onChange={onChange}
                loading={loading}
                rowKey={(record) => String(record.key ?? record.id)}
                components={{ body: { row: SortableTableRow } }}
                scroll={{ x: "max-content" }}
              />
            </SortableDisabledContext.Provider>
          </SortableContext>
        </DndContext>
      </WithEmptyStateWrapper>
    </section>
  )
}

EquipmentTable.propTypes = {
  data: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  onDeactivate: PropTypes.func,
  onDelete: PropTypes.func,
  onActivate: PropTypes.func,
  onAssignClick: PropTypes.func,
  onReorder: PropTypes.func,
  dragDisabled: PropTypes.bool,
  loading: PropTypes.bool
}

export default React.memo(EquipmentTable)
